import {
  ComponentDescriptor,
  useFields,
  FieldHandler,
  getRendererFromDescriptor,
  useValidators,
  useTransformers,
  loadComponent,
  IPageBuilderContext,
  PageBuilderContext,
  checkFormValues,
  useWizardService,
  SetFormFieldsEvent,
} from '@kizi/comparator-core';
import { FormWidget } from '../../FormWidget';
import React, { useContext, useMemo, useRef } from 'react';
import { Button, Div, DivProps, Span } from '@kizi/core-components';
import { SliderControl } from '@kizi/shared-components';
import { groupBy } from 'lodash';

export interface PersonalDataProps extends DivProps {
  fieldNames: string[];
  formLayout: ComponentDescriptor[];
  editModeFooter?: ComponentDescriptor;
  viewModeFooter?: ComponentDescriptor;
  viewModeLayout?: (ComponentDescriptor | string)[];
  header?: ComponentDescriptor;
  footer?: ComponentDescriptor;
  redirect?: boolean;
}

const formComponentNames = ['FormInput', 'FormComponent', 'FormSubmit'];

const parseFormComponents = (
  descriptors: ComponentDescriptor[],
  loadFn: (() => Promise<any>) | undefined,
) => {
  return descriptors.map((d) => {
    const parsedDescriptor = { ...d };
    if (formComponentNames.includes(d.type)) {
      const type = d.props.component.type;
      const Component = loadComponent(type, loadFn);
      if (Component)
        parsedDescriptor.props.loadedComponent = (
          <Component {...d.props.component.props} />
        );
    }
    if (d._children)
      parsedDescriptor._children = parseFormComponents(d._children, loadFn);
    return parsedDescriptor;
  });
};

const useDescriptor = (descriptor: ComponentDescriptor | undefined) => {
  return useMemo(() => {
    if (!descriptor) return descriptor;
    return getRendererFromDescriptor(descriptor);
  }, [descriptor]);
};

const parseDescriptorWithChildren = (descriptor: ComponentDescriptor) => {
  let parsedDescriptor = { ...descriptor };
  if (descriptor.props.loadedComponent) {
    parsedDescriptor = {
      ...descriptor,
      props: {
        ...descriptor.props,
        component: descriptor.props.loadedComponent,
        loadedComponent: undefined,
      },
    };
  }
  if (descriptor._children)
    parsedDescriptor._children = descriptor._children.map((child) =>
      parseDescriptorWithChildren(child),
    );
  return parsedDescriptor;
};

export const PersonalData: React.FC<PersonalDataProps> = ({
  fieldNames,
  formLayout,
  viewModeLayout: viewModeLayoutDescriptor,
  editModeFooter: editModeFooterDescriptor,
  viewModeFooter: viewModeFooterDescriptor,
  header: headerDescriptor,
  footer: footerDescriptor,
  redirect = false,
  ...divProps
}) => {
  const fields = useFields(fieldNames).filter(
    (f: FieldHandler | undefined): f is FieldHandler => f !== undefined,
  );
  const wizardService = useWizardService();
  const validators = useValidators();
  const transformers = useTransformers();
  const { loadFn } = useContext<IPageBuilderContext>(PageBuilderContext);
  const editModeLayout = useMemo(() => {
    const parsedFormComponents = parseFormComponents(formLayout, loadFn);
    const layout: React.ReactNode[] = [];
    for (const descriptor of parsedFormComponents) {
      if (descriptor) {
        const parsedDescriptor = parseDescriptorWithChildren(descriptor);
        layout.push(getRendererFromDescriptor(parsedDescriptor));
      }
    }
    return layout;
  }, [formLayout, loadFn]);
  const header = useDescriptor(headerDescriptor);
  const footer = useDescriptor(footerDescriptor);
  const editModeFooter = useDescriptor(editModeFooterDescriptor);
  let viewModeFooter = useDescriptor(viewModeFooterDescriptor);
  if (redirect) {
    viewModeFooter = (
      <Button
        onClick={() => {
          wizardService?.send({
            type: 'GOTO',
            index: 1,
            meta: { reload: true },
          });
        }}
        pt={1}
        variant="transparent"
      >
        {'Begin opnieuw'}
      </Button>
    );
  }
  const viewModeLayout = (viewModeLayoutDescriptor ?? []).map((descriptor) =>
    typeof descriptor === 'string' ? descriptor : useDescriptor(descriptor),
  );
  const viewModeBtnRef = useRef<HTMLElement>(null);

  return (
    <Div {...divProps}>
      <FormWidget
        onSubmit={(s) => {
          const groupedFields = groupBy(s.context.fields, 'formName');
          const eventsToSend: SetFormFieldsEvent[] = [];
          for (const stepName in groupedFields) {
            eventsToSend.push({
              type: 'SET_FORM_FIELDS',
              fields: groupedFields[stepName],
              name: stepName,
            });
          }
          const hasIncorrectValues = checkFormValues({
            fields: s.context.fields,
            validators: s.context.validators,
          });
          if (viewModeBtnRef.current && !hasIncorrectValues) {
            if (eventsToSend.length > 0) wizardService?.send(eventsToSend);
            viewModeBtnRef.current.click();
          }
        }}
        header={header}
        footer={[
          footer,
          <SliderControl
            key={'personal-data-block'}
            goto={0}
            component={<Span ref={viewModeBtnRef} display="none" />}
          />,
        ]}
        editModeFooter={editModeFooter}
        editModeLayout={editModeLayout}
        viewModeFooter={viewModeFooter}
        viewModeLayout={viewModeLayout}
        fields={fields}
        validators={validators}
        transformers={transformers}
      />
    </Div>
  );
};
